import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { allGenres } from '@/utils/movies';

const navItems = [
  { label: '首页', to: '/' },
  { label: '分类', to: '/category/action', hasMenu: true },
  { label: '搜索', to: '/search' },
  { label: '关于', to: '/about' },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [genreOpen, setGenreOpen] = useState(false);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMobileOpen(false);
  };

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to.split('/').slice(0, 2).join('/'));
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-movie-2-line text-accent-500 text-xl w-5 h-5 flex items-center justify-center"></i>
          </span>
          <span className="font-heading font-extrabold text-lg text-primary-600 tracking-tight whitespace-nowrap">
            6080yy<span className="text-accent-500">电影</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.hasMenu && setGenreOpen(true)}
              onMouseLeave={() => item.hasMenu && setGenreOpen(false)}
            >
              <Link
                to={item.to}
                className={`px-4 py-2 rounded-md text-sm font-label font-medium transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap ${
                  isActive(item.to)
                    ? 'text-accent-600'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                {item.label}
                {item.hasMenu && <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>}
              </Link>
              {item.hasMenu && genreOpen && (
                <div className="absolute left-0 top-full pt-1 w-[440px]">
                  <div className="bg-background-50 border border-background-200 rounded-lg p-3 grid grid-cols-2 gap-1">
                    {allGenres.map((genre) => (
                      <Link
                        key={genre.key}
                        to={`/category/${genre.key}`}
                        onClick={() => setGenreOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <i className={`${genre.icon} text-accent-500 w-4 h-4 flex items-center justify-center`}></i>
                        <span className="whitespace-nowrap">{genre.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="hidden lg:flex items-center gap-2 bg-background-100 rounded-full pl-4 pr-1 py-1 w-72 border border-background-200">
          <i className="ri-search-line text-foreground-400 text-base w-5 h-5 flex items-center justify-center"></i>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索电影、导演、演员"
            className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            className="px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((value) => !value)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
          aria-label="打开菜单"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl w-6 h-6 flex items-center justify-center`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <form onSubmit={handleSearch} className="flex items-center gap-2 bg-background-100 rounded-full pl-4 pr-1 py-1 mb-4 border border-background-200">
            <i className="ri-search-line text-foreground-400 text-base w-5 h-5 flex items-center justify-center"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影、导演、演员"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col gap-1 mb-4">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <p className="text-xs font-label text-foreground-500 mb-2 px-3">热门分类</p>
          <div className="grid grid-cols-3 gap-2">
            {allGenres.map((genre) => (
              <Link
                key={genre.key}
                to={`/category/${genre.key}`}
                onClick={() => setMobileOpen(false)}
                className="flex flex-col items-center gap-1 py-3 rounded-md bg-background-100 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className={`${genre.icon} text-accent-500 text-lg w-5 h-5 flex items-center justify-center`}></i>
                <span className="text-xs font-label">{genre.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}